import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApi } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft, Phone, Mail, MapPin, FileText, Upload, MessageSquare, Activity,
  Sparkles, CheckCircle2, XCircle, Send, Plus, ShieldCheck, FileSignature,
  AlertTriangle, Trash2
} from "lucide-react";
import { toast } from "sonner";
import { money, stageLabel, STAGE_COLORS, STAGES, fmtDate, fmtDateTime } from "@/lib/format";

function ConfidenceBadge({ level }) {
  const styles = {
    Verified: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Probable: "bg-amber-100 text-amber-800 border-amber-200",
    Unverified: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return <Badge variant="outline" className={`text-[10px] ${styles[level] || styles.Unverified}`}>{level || "Unverified"}</Badge>;
}

export default function ContactProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { get, patch, post, upload, del, token } = useApi();
  const [contact, setContact] = useState(null);
  const [docs, setDocs] = useState([]);
  const [comms, setComms] = useState([]);
  const [audit, setAudit] = useState([]);
  const [packages, setPackages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tab, setTab] = useState("overview");

  const reload = useCallback(async () => {
    const [c, d, m, a, p, t] = await Promise.all([
      get(`/contacts/${id}`),
      get(`/contacts/${id}/documents`),
      get(`/contacts/${id}/communications`),
      get(`/contacts/${id}/audit`),
      get(`/contacts/${id}/claim-packages`),
      get(`/templates`),
    ]);
    setContact(c.data);
    setDocs(d.data || []);
    setComms(m.data || []);
    setAudit(a.data || []);
    setPackages(p.data || []);
    setTemplates(t.data || []);
  }, [id, get]);

  useEffect(() => { reload().catch(() => toast.error("Could not load contact")); }, [reload]);

  // Stable prop bundles to avoid re-rendering child tabs on every render
  const commsApi = useMemo(() => ({ get, post, patch }), [get, post, patch]);
  const docsApi = useMemo(() => ({ upload, del, token }), [upload, del, token]);
  const claimApi = useMemo(() => ({ post }), [post]);

  if (!contact) return <div className="text-slate-500">Loading contact…</div>;

  const propAddr = [contact.property_street, contact.property_city, contact.property_state, contact.property_zip].filter(Boolean).join(", ");

  const updateField = async (field, value) => {
    try {
      const r = await patch(`/contacts/${id}`, { [field]: value });
      setContact(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} data-testid="contact-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Contact</p>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-0.5">
              {contact.client_name || "(unnamed)"}
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              {contact.first_name} {contact.last_name} · {contact.possible_type || "Owner on record"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`${STAGE_COLORS[contact.stage] || ""}`}>
            {stageLabel(contact.stage)}
          </Badge>
          <Select value={contact.stage} onValueChange={(v) => updateField("stage", v)}>
            <SelectTrigger className="h-9 w-44" data-testid="contact-stage-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Snapshot Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 border-slate-200">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Surplus</p>
          <p className="font-mono text-2xl font-black text-emerald-700 mt-1">{money(contact.surplus_amount)}</p>
          <ConfidenceBadge level={contact.confidence?.surplus_amount} />
        </Card>
        <Card className="p-4 border-slate-200">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Est. Net (after {Math.round(contact.contingency_fee_pct || 35)}% fee)</p>
          <p className="font-mono text-2xl font-black text-slate-900 mt-1">
            {money((contact.surplus_amount || 0) * (1 - (contact.contingency_fee_pct || 35) / 100))}
          </p>
        </Card>
        <Card className="p-4 border-slate-200">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Qualification</p>
          <div className="flex items-baseline gap-2 mt-1">
            <p className="font-mono text-2xl font-black text-slate-900">{contact.qualification_score ?? "—"}</p>
            <span className="text-xs uppercase tracking-wider text-slate-500">{contact.qualification}</span>
          </div>
        </Card>
        <Card className="p-4 border-slate-200">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Deadline</p>
          <p className="font-mono text-lg font-bold text-slate-900 mt-1">{fmtDate(contact.claim_deadline)}</p>
          <ClaimDeadlineEditor contact={contact} onSave={(v) => updateField("claim_deadline", v)} />
        </Card>
      </div>

      {contact.qualification_notes && (
        <Card className="p-3 border-amber-200 bg-amber-50">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
            <p className="text-sm text-amber-900"><b>Notes:</b> {contact.qualification_notes}</p>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="communications" data-testid="tab-communications">Communications</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="claim" data-testid="tab-claim">Claim Package</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 pt-4">
          <OverviewTab contact={contact} propAddr={propAddr} />
        </TabsContent>

        <TabsContent value="communications" className="pt-4">
          <CommunicationsTab
            contact={contact}
            comms={comms}
            templates={templates}
            api={commsApi}
            reload={reload}
          />
        </TabsContent>

        <TabsContent value="documents" className="pt-4">
          <DocumentsTab
            contact={contact}
            docs={docs}
            api={docsApi}
            reload={reload}
          />
        </TabsContent>

        <TabsContent value="claim" className="pt-4">
          <ClaimTab
            contact={contact}
            packages={packages}
            api={claimApi}
            reload={reload}
          />
        </TabsContent>

        <TabsContent value="audit" className="pt-4">
          <AuditTab entries={audit} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ClaimDeadlineEditor({ contact, onSave }) {
  const [v, setV] = useState(contact.claim_deadline?.slice(0, 10) || "");
  return (
    <div className="mt-2 flex gap-2">
      <Input
        type="date" value={v} onChange={(e) => setV(e.target.value)}
        className="h-7 text-xs"
        data-testid="deadline-input"
      />
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onSave(v ? new Date(v).toISOString() : null)} data-testid="deadline-save-btn">Save</Button>
    </div>
  );
}

function OverviewTab({ contact, propAddr }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2 p-5 border-slate-200">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Property & Case</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Field label="Property Address" value={propAddr} confidence={contact.confidence?.property_address} />
          <Field label="County" value={contact.county} />
          <Field label="State" value={contact.property_state} />
          <Field label="Case Number" value={contact.case_number} mono confidence={contact.confidence?.case_number} />
          <Field label="Parcel Number" value={contact.parcel_number} mono />
          <Field label="Foreclosure Type" value={contact.foreclosure_type} />
          <Field label="Sale Date" value={fmtDate(contact.sale_date)} />
          <Field label="Opening Bid" value={money(contact.opening_bid)} mono />
          <Field label="Closing Bid" value={money(contact.closing_bid)} mono />
          <Field label="Assessed Value" value={money(contact.assessed_value)} mono />
        </div>
      </Card>
      <Card className="p-5 border-slate-200">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Claimant Contact</p>
        <div className="space-y-3 text-sm">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Phones</p>
            {contact.phones?.length ? contact.phones.map((p, i) => (
              <div key={`${p.number || "np"}-${i}`} className="flex items-center gap-2 mt-1">
                <Phone className="h-3.5 w-3.5 text-slate-400" />
                <span className="font-mono">{p.number}</span>
                <span className="text-xs text-slate-500">{p.type}</span>
                {p.dnc === "Y" && <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px]">DNC</Badge>}
              </div>
            )) : <p className="text-slate-400 text-sm">No phones</p>}
          </div>
          <Separator />
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Emails</p>
            {contact.emails?.length ? contact.emails.map((e, i) => (
              <div key={`${e}-${i}`} className="flex items-center gap-2 mt-1">
                <Mail className="h-3.5 w-3.5 text-slate-400" />
                <span className="font-mono text-xs">{e}</span>
              </div>
            )) : <p className="text-slate-400 text-sm">No emails</p>}
          </div>
        </div>
      </Card>

      {contact.complications?.length > 0 && (
        <Card className="lg:col-span-3 p-4 border-amber-200 bg-amber-50">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-800 mb-2">Complications flagged</p>
          <div className="flex flex-wrap gap-2">
            {contact.complications.map((c) => (
              <Badge key={c} variant="outline" className="bg-white border-amber-300 text-amber-800 text-xs">
                {c.replace(/_/g, " ")}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      {Object.keys(contact.raw_extra || {}).length > 0 && (
        <Card className="lg:col-span-3 p-5 border-slate-200">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Other data from import</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
            {Object.entries(contact.raw_extra).map(([k, v]) => (
              <div key={k}>
                <p className="text-slate-400 uppercase tracking-wider text-[10px] font-semibold">{k}</p>
                <p className="font-mono text-slate-800">{v}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Field({ label, value, confidence, mono = false }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</p>
        {confidence && <ConfidenceBadge level={confidence} />}
      </div>
      <p className={`mt-0.5 ${mono ? "font-mono text-sm" : "text-sm"} text-slate-900`}>
        {value || <span className="text-slate-400">—</span>}
      </p>
    </div>
  );
}

function CommunicationsTab({ contact, comms, templates, api, reload }) {
  const { get, post, patch } = api;
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState("email");
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [checks, setChecks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const startDraft = async () => {
    setBusy(true);
    try {
      const r = await post("/communications/draft", {
        contact_id: contact.id, channel, template_id: templateId || null,
        subject: subject || null, body: body || null, touchpoint: 1,
      });
      setDraftId(r.data.id);
      setSubject(r.data.subject || "");
      setBody(r.data.body || "");
      setChecks(r.data.compliance_checks || []);
      toast.success("Draft created. Edit & submit for approval.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed");
    } finally { setBusy(false); }
  };

  const updateDraft = async () => {
    if (!draftId) return;
    setBusy(true);
    try {
      const r = await patch(`/communications/${draftId}`, { subject, body });
      setChecks(r.data.compliance_checks || []);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const aiAssist = async () => {
    setAiBusy(true);
    try {
      const r = await post("/ai/draft", {
        contact_id: contact.id, channel, intent: "first_contact",
      });
      setBody(r.data.text);
      toast.success("AI draft generated. Review carefully before submitting.");
    } catch (e) {
      toast.error("AI failed");
    } finally { setAiBusy(false); }
  };

  const submitForApproval = async () => {
    if (!draftId) return;
    setBusy(true);
    try {
      // Save first if edits pending
      await patch(`/communications/${draftId}`, { subject, body });
      await post(`/communications/${draftId}/submit-approval`, {});
      toast.success("Submitted for Owner approval.");
      setOpen(false);
      setDraftId(null);
      setSubject(""); setBody(""); setChecks([]);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  const filteredTemplates = templates.filter(t => t.channel === channel);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">All outbound communications are staged for Owner approval.</p>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setDraftId(null); setSubject(""); setBody(""); setChecks([]); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-communication-btn" className="bg-slate-900 hover:bg-slate-800">
              <Plus className="h-4 w-4 mr-1.5" /> New communication
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="font-display">Compose to {contact.client_name}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Channel</Label>
                <Select value={channel} onValueChange={(v) => { setChannel(v); setTemplateId(""); }}>
                  <SelectTrigger data-testid="comm-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="mail">US Mail</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Template (optional)</Label>
                <Select value={templateId || "none"} onValueChange={(v) => setTemplateId(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="comm-template-select"><SelectValue placeholder="No template (custom)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Custom (no template)</SelectItem>
                    {filteredTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!draftId && (
              <div className="flex justify-end">
                <Button onClick={startDraft} disabled={busy} data-testid="comm-create-draft-btn">
                  {busy ? "Generating…" : "Create draft"}
                </Button>
              </div>
            )}

            {draftId && (
              <>
                {channel === "email" && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Subject</Label>
                    <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="comm-subject-input" />
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Body</Label>
                    <Button size="sm" variant="ghost" onClick={aiAssist} disabled={aiBusy} data-testid="comm-ai-assist-btn">
                      <Sparkles className="h-3.5 w-3.5 mr-1 text-blue-600" />
                      {aiBusy ? "AI thinking…" : "AI rewrite"}
                    </Button>
                  </div>
                  <Textarea
                    value={body} onChange={(e) => setBody(e.target.value)}
                    className="min-h-[260px] font-mono text-sm"
                    data-testid="comm-body-input"
                  />
                </div>

                {checks.length > 0 && (
                  <div className="bg-slate-50 border border-slate-200 rounded p-3">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Compliance check</p>
                    <ul className="space-y-1 text-xs">
                      {checks.map((c, i) => (
                        <li key={`${c.check}-${i}`} className="flex items-start gap-2">
                          {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5" /> : <XCircle className="h-3.5 w-3.5 text-red-600 mt-0.5" />}
                          <div><b>{c.check}.</b> <span className="text-slate-500">{c.note}</span></div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <DialogFooter>
                  <Button variant="outline" onClick={updateDraft} disabled={busy} data-testid="comm-save-draft-btn">Save draft</Button>
                  <Button
                    onClick={submitForApproval} disabled={busy}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="comm-submit-approval-btn"
                  >
                    <Send className="h-4 w-4 mr-1.5" /> Submit for Owner approval
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 p-0 overflow-hidden">
        {comms.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            <p className="font-semibold text-slate-700">No communications yet.</p>
            <p className="text-xs mt-1">Click &quot;New communication&quot; to draft a message.</p>
          </div>
        ) : (
          <table className="cw-table w-full">
            <thead>
              <tr>
                <th>Channel</th>
                <th>Subject / Body</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {comms.map((c) => (
                <tr key={c.id} data-testid={`comm-row-${c.id}`}>
                  <td><Badge variant="outline" className="text-xs">{c.channel.toUpperCase()}</Badge></td>
                  <td className="max-w-md truncate">
                    <div className="font-semibold text-slate-900 text-sm">{c.subject || "(no subject)"}</div>
                    <div className="text-xs text-slate-500 truncate">{c.body?.slice(0, 100)}…</div>
                  </td>
                  <td>
                    <Badge variant="outline" className={
                      c.status === "sent" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                      c.status === "pending_approval" ? "bg-amber-100 text-amber-800 border-amber-200" :
                      c.status === "draft" ? "bg-slate-100 text-slate-700 border-slate-200" :
                      "bg-red-100 text-red-800 border-red-200"
                    }>{c.status.replace("_", " ")}</Badge>
                  </td>
                  <td className="text-xs font-mono text-slate-500">{fmtDateTime(c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function DocumentsTab({ contact, docs, api, reload }) {
  const { upload, del, token } = api;
  const [busy, setBusy] = useState(false);
  const [type, setType] = useState("other");
  const ref = useRef(null);

  const onPick = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("doc_type", type);
      await upload(`/contacts/${contact.id}/documents`, fd);
      toast.success("Document uploaded");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const onDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await del(`/documents/${docId}`);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const downloadUrl = (docId) => `${process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/download?auth=${token}`;

  return (
    <div className="space-y-4">
      <Card className="p-4 border-slate-200">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs uppercase tracking-widest text-slate-500">Document type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="doc-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="deed">Deed</SelectItem>
                <SelectItem value="court_record">Court Record</SelectItem>
                <SelectItem value="id">Government ID</SelectItem>
                <SelectItem value="affidavit">Affidavit</SelectItem>
                <SelectItem value="claim_form">Claim Form</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
                <SelectItem value="signed_contract">Signed Contract</SelectItem>
                <SelectItem value="claimant_upload">Claimant Upload</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <input ref={ref} type="file" hidden onChange={(e) => onPick(e.target.files?.[0])} data-testid="doc-file-input" />
          <Button onClick={() => ref.current?.click()} disabled={busy} data-testid="doc-upload-btn" className="bg-slate-900 hover:bg-slate-800">
            <Upload className="h-4 w-4 mr-1.5" /> {busy ? "Uploading…" : "Upload"}
          </Button>
        </div>
      </Card>

      <Card className="border-slate-200 p-0 overflow-hidden">
        {docs.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <FileText className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            <p className="font-semibold text-slate-700">No documents yet.</p>
            <p className="text-xs mt-1">Upload deeds, IDs, court records, signed contracts.</p>
          </div>
        ) : (
          <table className="cw-table w-full">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th className="text-right">Size</th>
                <th>Uploaded</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                  <td className="font-semibold">{d.name}</td>
                  <td><Badge variant="outline" className="text-xs">{d.doc_type}</Badge></td>
                  <td className="text-right font-mono text-xs">{(d.size / 1024).toFixed(1)} KB</td>
                  <td className="text-xs text-slate-500 font-mono">{fmtDateTime(d.created_at)}</td>
                  <td className="text-right">
                    <a href={downloadUrl(d.id)} target="_blank" rel="noreferrer" className="inline-block">
                      <Button size="sm" variant="ghost" data-testid={`doc-view-${d.id}`}>View</Button>
                    </a>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(d.id)} data-testid={`doc-delete-${d.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function ClaimTab({ contact, packages, api, reload }) {
  const { post } = api;
  const [busy, setBusy] = useState(false);

  const create = async () => {
    setBusy(true);
    try {
      await post("/claim-packages", { contact_id: contact.id });
      toast.success("Claim package created");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const submit = async (pid) => {
    try {
      await post(`/claim-packages/${pid}/submit-approval`, {});
      toast.success("Submitted for Owner approval");
      reload();
    } catch (e) { toast.error("Submit failed"); }
  };

  const fileWithCourt = async (pid) => {
    try {
      await post(`/claim-packages/${pid}/file`, {});
      toast.success("Filing request submitted for approval");
      reload();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 border-slate-200">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Phase 5 — Closing</p>
            <h3 className="font-display text-lg font-bold text-slate-900 mt-1">Generate claim package</h3>
            <p className="text-sm text-slate-600 mt-1 max-w-xl">
              ClaimWerks will assemble the contingency agreement, county claim forms,
              and supporting docs — pre-populated from this contact&apos;s data and ready
              for your final review.
            </p>
          </div>
          <Button onClick={create} disabled={busy} data-testid="claim-create-btn" className="bg-slate-900 hover:bg-slate-800">
            <FileSignature className="h-4 w-4 mr-1.5" /> {busy ? "Building…" : "New claim package"}
          </Button>
        </div>
      </Card>

      {packages.length === 0 ? (
        <Card className="p-10 border-slate-200 text-center">
          <FileSignature className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          <p className="font-semibold text-slate-700">No claim packages yet.</p>
        </Card>
      ) : packages.map((p) => (
        <Card key={p.id} className="p-5 border-slate-200" data-testid={`claim-package-${p.id}`}>
          <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-display text-lg font-bold text-slate-900">{p.claim_form_data.form_name}</p>
                <Badge variant="outline" className={
                  p.status === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                  p.status === "pending_approval" ? "bg-amber-100 text-amber-800 border-amber-200" :
                  p.status === "filed" ? "bg-cyan-100 text-cyan-800 border-cyan-200" :
                  "bg-slate-100 text-slate-700 border-slate-200"
                }>{p.status.replace(/_/g, " ")}</Badge>
              </div>
              <p className="text-xs text-slate-500 mt-1 font-mono">Created {fmtDateTime(p.created_at)}</p>
            </div>
            <div className="flex gap-2">
              {p.status === "draft" && (
                <Button onClick={() => submit(p.id)} className="bg-amber-600 hover:bg-amber-700" data-testid={`claim-submit-${p.id}`}>
                  <Send className="h-4 w-4 mr-1.5" /> Submit for approval
                </Button>
              )}
              {p.status === "approved" && (
                <Button onClick={() => fileWithCourt(p.id)} className="bg-cyan-600 hover:bg-cyan-700" data-testid={`claim-file-${p.id}`}>
                  <FileSignature className="h-4 w-4 mr-1.5" /> File with court
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Contract data</p>
              <dl className="text-sm space-y-1.5 font-mono">
                <PRow l="Claimant" v={p.contract_data.claimant_name} />
                <PRow l="Property" v={p.contract_data.property_address} />
                <PRow l="Case #" v={p.contract_data.case_number} />
                <PRow l="Parcel #" v={p.contract_data.parcel_number} />
                <PRow l="Surplus" v={`$${(p.contract_data.estimated_surplus || 0).toLocaleString()}`} />
                <PRow l="Fee" v={`${p.contract_data.contingency_fee_pct?.toFixed(0)}% · $${p.estimated_fee.toLocaleString()}`} />
                <PRow l="Net to claimant" v={`$${p.estimated_net.toLocaleString()}`} highlight />
              </dl>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Claim form</p>
              <dl className="text-sm space-y-1.5 font-mono">
                <PRow l="Form" v={p.claim_form_data.form_name} />
                <PRow l="Signature required" v={p.claim_form_data.claimant_signature_required ? "Yes" : "No"} />
                <PRow l="Notarization required" v={p.claim_form_data.notarization_required ? "Yes" : "No"} />
                {p.filing_date && <PRow l="Filed on" v={fmtDate(p.filing_date)} />}
                {p.filing_confirmation && <PRow l="Confirmation" v={p.filing_confirmation} />}
              </dl>
              <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded text-xs">
                <p className="font-semibold text-slate-600 mb-1">State disclosure:</p>
                <p className="text-slate-700">{p.contract_data.state_disclosure}</p>
              </div>
            </div>
          </div>

          {/* Filled documents (autofilled DOCX + PDF per template) */}
          {(p.filled_documents || []).length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-200">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                Autofilled documents ({(p.filled_documents || []).length})
              </p>
              <FilledDocs items={p.filled_documents} />
            </div>
          )}

          {/* Attached forms from Library */}
          <div className="mt-5 pt-4 border-t border-slate-200">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
              Attached forms ({(p.attached_forms || []).length})
            </p>
            {(!p.attached_forms || p.attached_forms.length === 0) ? (
              <div className="text-sm text-slate-500 bg-amber-50 border border-amber-200 rounded p-3">
                <AlertTriangle className="h-3.5 w-3.5 inline mr-1 text-amber-600" />
                No county form uploaded yet for{" "}
                <span className="font-mono font-semibold">{contact.county}, {contact.property_state}</span>.{" "}
                Add it in the <a href="/forms" className="underline font-semibold">Forms Library</a>.
              </div>
            ) : (
              <AttachedForms forms={p.attached_forms} contactCounty={contact.county} contactState={contact.property_state} />
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

function FilledDocs({ items }) {
  const { token } = useApi();
  const dl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/documents/${id}/download?auth=${token}`;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {items.map((f) => (
        <div key={f.slug + f.document_id}
             data-testid={`filled-doc-${f.slug}`}
             className="p-2.5 bg-emerald-50/40 border border-emerald-200 rounded">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{f.name}</div>
              <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                {f.mapped ? "✓ Fields autofilled" : "⚠ No mapping yet — raw template"}
              </div>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <a href={dl(f.document_id)} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                  data-testid={`filled-doc-docx-${f.slug}`}>DOCX</Button>
              </a>
              {f.pdf_document_id && (
                <a href={dl(f.pdf_document_id)} target="_blank" rel="noreferrer">
                  <Button size="sm" className="h-7 text-[11px] bg-red-600 hover:bg-red-700 text-white"
                    data-testid={`filled-doc-pdf-${f.slug}`}>PDF</Button>
                </a>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AttachedForms({ forms, contactCounty, contactState }) {
  const { token } = useApi();
  const countyForms = forms.filter(f => f.kind === "county_form");
  const dl = (kind, id) => {
    const path = kind === "county_form" ? "forms/county" : "forms/templates";
    return `${process.env.REACT_APP_BACKEND_URL}/api/${path}/${id}/download?auth=${token}`;
  };
  return (
    <div>
      {countyForms.length === 0 ? (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          ⚠ No county form uploaded yet for {contactCounty}, {contactState}.
        </div>
      ) : countyForms.map((f) => (
        <a key={f.form_id} href={dl(f.kind, f.form_id)} target="_blank" rel="noreferrer"
           data-testid={`attached-county-form-${f.form_id}`}
           className="flex items-center justify-between p-2 bg-white border border-slate-200 rounded hover:border-slate-400 mb-1">
          <div>
            <div className="text-sm font-semibold text-slate-900">{f.form_name}</div>
            <div className="text-xs text-slate-500 font-mono truncate">{f.original_filename}</div>
          </div>
          <span className="text-xs font-semibold text-blue-600">View →</span>
        </a>
      ))}
    </div>
  );
}

function PRow({ l, v, highlight }) {
  return (
    <div className="flex justify-between gap-3 text-xs">
      <dt className="text-slate-500 uppercase tracking-wider">{l}</dt>
      <dd className={`text-right ${highlight ? "text-emerald-700 font-bold" : "text-slate-900"}`}>{v || "—"}</dd>
    </div>
  );
}

function AuditTab({ entries }) {
  return (
    <Card className="p-5 border-slate-200">
      <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Full audit trail</p>
      {entries.length === 0 ? (
        <p className="text-sm text-slate-500">No activity yet.</p>
      ) : (
        <div className="relative pl-5 border-l-2 border-slate-200 space-y-4 max-h-[600px] overflow-auto">
          {entries.map((e) => (
            <div key={e.id} className="relative" data-testid={`audit-row-${e.id}`}>
              <span className="absolute -left-[1.7rem] top-1 h-3 w-3 rounded-full bg-slate-900 ring-2 ring-white" />
              <div className="text-sm font-semibold text-slate-900">{e.action.replace(/[_.]/g, " ")}</div>
              <div className="text-xs text-slate-500 font-mono mt-0.5">
                {fmtDateTime(e.timestamp)} · by <span className="font-semibold">{e.actor}</span>
              </div>
              {e.details && Object.keys(e.details).length > 0 && (
                <pre className="mt-1 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 overflow-auto">
                  {JSON.stringify(e.details, null, 2)}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
